import React, { useState } from 'react';
import styled from 'styled-components';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category?: string;
}

interface FAQProps {
  title?: string;
  subtitle?: string;
  items: FAQItem[];
  showCategories?: boolean;
}

const FAQContainer = styled.section`
  padding: 5rem 1rem;
  background-color: #f8f9fa;
`;

const FAQHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 4rem;
`;

const FAQTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  
  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

const FAQSubtitle = styled.p`
  font-size: 1.2rem;
  color: var(--gray);
  margin-bottom: 2rem;
`;

const CategoryFilter = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 3rem;
`;

const CategoryButton = styled.button<{ active: boolean }>`
  background-color: ${props => props.active ? 'var(--blue)' : 'transparent'};
  color: ${props => props.active ? 'white' : 'var(--gray-dark)'};
  border: ${props => props.active ? 'none' : '1px solid #e0e0e0'};
  border-radius: 2rem;
  padding: 0.6rem 1.2rem;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.active ? 'var(--blue)' : '#f0f0f0'};
  }
`;

const FAQList = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const FAQItemContainer = styled.div`
  background-color: white;
  border-radius: 0.75rem;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  margin-bottom: 1.5rem;
  overflow: hidden;
  transition: box-shadow 0.3s ease;
  
  &:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
`;

const FAQQuestion = styled.button<{ isOpen: boolean }>`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  background-color: ${props => props.isOpen ? '#f8f9fa' : 'white'};
  border: none;
  text-align: left;
  font-size: 1.1rem;
  font-weight: ${props => props.isOpen ? '600' : '500'};
  color: var(--gray-dark);
  cursor: pointer;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: ${props => props.isOpen ? '#f8f9fa' : '#f9f9f9'};
  }
  
  svg {
    color: ${props => props.isOpen ? 'var(--blue)' : 'var(--gray)'};
    font-size: 1rem;
    flex-shrink: 0;
    margin-left: 1rem;
  }
`;

const FAQAnswer = styled.div<{ isOpen: boolean }>`
  padding: ${props => props.isOpen ? '0 1.5rem 1.5rem' : '0 1.5rem'};
  max-height: ${props => props.isOpen ? '1000px' : '0'};
  overflow: hidden;
  transition: all 0.3s ease;
`;

const FAQAnswerContent = styled.div`
  color: var(--gray);
  line-height: 1.6;
  font-size: 1rem;
  
  p {
    margin-bottom: 1rem;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  ul {
    margin-bottom: 1rem;
    padding-left: 1.5rem;
    
    li {
      margin-bottom: 0.5rem;
    }
  }
  
  a {
    color: var(--blue);
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const FAQ: React.FC<FAQProps> = ({
  title = "Preguntas frecuentes",
  subtitle = "Respuestas a las dudas más comunes sobre nuestros servicios",
  items,
  showCategories = true
}) => {
  const [openItems, setOpenItems] = useState<string[]>([items[0]?.id || '']);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  
  // Extraer categorías únicas
  const categories = Array.from(new Set(items.map(item => item.category || 'General')));
  
  // Filtrar elementos por categoría
  const filteredItems = activeCategory === 'all'
    ? items
    : items.filter(item => (item.category || 'General') === activeCategory);
  
  const toggleItem = (id: string) => {
    if (openItems.includes(id)) {
      setOpenItems(openItems.filter(itemId => itemId !== id));
    } else {
      setOpenItems([...openItems, id]);
    }
  };
  
  return (
    <FAQContainer>
      <FAQHeader>
        <FAQTitle>{title}</FAQTitle>
        {subtitle && <FAQSubtitle>{subtitle}</FAQSubtitle>}
        
        {showCategories && categories.length > 1 && (
          <CategoryFilter>
            <CategoryButton 
              active={activeCategory === 'all'} 
              onClick={() => setActiveCategory('all')}
            >
              Todas
            </CategoryButton>
            {categories.map(category => (
              <CategoryButton
                key={category}
                active={activeCategory === category}
                onClick={() => setActiveCategory(category)}
              >
                {category}
              </CategoryButton>
            ))}
          </CategoryFilter>
        )}
      </FAQHeader>
      
      <FAQList>
        {filteredItems.map(item => {
          const isOpen = openItems.includes(item.id);
          
          return (
            <FAQItemContainer key={item.id}>
              <FAQQuestion 
                isOpen={isOpen} 
                onClick={() => toggleItem(item.id)}
              >
                {item.question}
                {isOpen ? <FaChevronUp /> : <FaChevronDown />}
              </FAQQuestion>
              <FAQAnswer isOpen={isOpen}>
                <FAQAnswerContent dangerouslySetInnerHTML={{ __html: item.answer }} />
              </FAQAnswer>
            </FAQItemContainer>
          );
        })}
      </FAQList>
    </FAQContainer>
  );
};

export default FAQ; 